import { z } from 'zod';

// Coercion to the type a field declares. JSON carries no dates, no numbers in
// a text input and no booleans in a select, so a body reaches us with the
// value stringified: each wrapper below declares the value it really wants and
// coerces what a form or a partner actually sends into it. yup cast those
// before validating; these keep that acceptance now that zod does the
// validating, WITHOUT zod's own `z.coerce.*`, whose edge cases are wrong for us
// (`z.coerce.number()` reads '' as 0, `z.coerce.boolean()` reads 'false' as
// true, `z.coerce.string()` reads null as the text "null"). A value these
// cannot coerce is passed through untouched, for the schema to report.
//
// A wrapper takes the schema it guards, so every constraint stays declared on
// the value itself and reaches the OpenAPI spec:
// `coerceNumber(z.number().int())` documents as an integer, exactly as the
// bare schema would.

const readAsString = (value: unknown) =>
  typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : value;

// A number as a text input or a partner's JSON sends it: '7', ' 7 '. Blank and
// non-numeric text stay as they came so the number schema reports them.
const readAsNumber = (value: unknown) => {
  if (typeof value !== 'string') {
    return value;
  }
  const trimmed = value.trim();
  if (trimmed === '') {
    return value;
  }
  const parsed = Number(trimmed);
  return Number.isNaN(parsed) ? value : parsed;
};

// Only the two words a boolean is spelled as, never JS truthiness: 'yes' and
// '' are not booleans and must be reported, not silently read as true/false.
const readAsBoolean = (value: unknown) => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return value;
};

/** A string, coercing the number or boolean a partner may send in its place. */
export const coerceString = <T extends z.ZodType>(schema: T) =>
  z.preprocess(readAsString, schema);

/** A number, coercing the numeric string a form field or partner sends. */
export const coerceNumber = <T extends z.ZodType>(schema: T) =>
  z.preprocess(readAsNumber, schema);

/** A boolean, coercing the 'true'/'false' text a partner may send. */
export const coerceBoolean = <T extends z.ZodType>(schema: T) =>
  z.preprocess(readAsBoolean, schema);
